import React from 'react'
import {
  Slide,
  Box,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Portal,
  Alert,
  Typography as MIUTypography,
} from '@mui/material'
import AddBusinessIcon from '@mui/icons-material/AddBusiness'
import { useLocation, useNavigate } from 'react-router-dom'
import { useCities } from '../../context/CitiesContext'
import { useTreePermissions } from '../../hooks/useTreePermissions'
import { TREE_PERMISSION } from '../../utils/treePermissions'
import { detectCityFromURL } from '../../utils/cityDetector'
import { buildCantonUrl } from '../../utils/cantonUrl'
import TreeRegistrationWizardV2 from '../../components/TreeRegistration'
import { TERRITORIAL_MANAGEMENT_DEFAULT_ROUTE } from '../../config/routes'

const RegisterTreeWizardViewV2 = () => {
  const theme = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const {
    selectedTenant,
    selectTenant,
    getTenantsByCantonSlug,
    cantonesStatus,
    reloadCantones,
    loading: citiesLoading,
    getEnabledCantones,
  } = useCities()
  const cantones = getEnabledCantones()
  const { getPermittedTenants } = useTreePermissions()

  const currentCity = detectCityFromURL(location.pathname) || 'loja'
  const availableTenants = getTenantsByCantonSlug(currentCity)
  const registrableTenants = React.useMemo(
    () => getPermittedTenants(availableTenants, TREE_PERMISSION.REGISTER),
    [getPermittedTenants, availableTenants],
  )
  const hasCantonesIssue = cantonesStatus === 'error' || cantonesStatus === 'empty'
  const hasNoTenants = availableTenants.length === 0

  // Autoselecciona el primer tenant con permiso de registrar si no hay uno elegido
  // o si el elegido (p. ej. heredado de sessionStorage) no tiene ese permiso
  React.useEffect(() => {
    if (registrableTenants.length === 0) return
    if (registrableTenants.some(tenant => tenant.slug === selectedTenant)) return
    selectTenant(registrableTenants[0].slug)
  }, [registrableTenants, selectedTenant, selectTenant])

  if (hasCantonesIssue) {
    const isError = cantonesStatus === 'error'
    return (
      <Portal>
        <Dialog
          open={true}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' },
          }}>
          <DialogTitle
            sx={{
              bgcolor: isError ? 'error.main' : 'warning.main',
              color: isError ? 'error.contrastText' : 'warning.contrastText',
              fontWeight: 600,
            }}>
            {isError
              ? 'Error al cargar instituciones'
              : 'No se pudieron cargar instituciones'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <Alert severity={isError ? 'error' : 'warning'} sx={{ mb: 2, mt: 2 }}>
              No se pudo obtener la lista de instituciones para el cantón{' '}
              <strong>{currentCity}</strong>. Puede ser un problema temporal o de caché.
            </Alert>
            <MIUTypography variant="body2" color="text.secondary">
              Intente recargar la información. Si persiste, contacte al administrador.
            </MIUTypography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => navigate(buildCantonUrl(currentCity, cantones))}>
              Volver al mapa
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={reloadCantones}
              disabled={citiesLoading}>
              Reintentar
            </Button>
          </DialogActions>
        </Dialog>
      </Portal>
    )
  }

  if (hasNoTenants) {
    return (
      <Portal>
        <Dialog
          open={true}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' },
          }}>
          <DialogTitle
            sx={{
              bgcolor: 'warning.main',
              color: 'warning.contrastText',
              fontWeight: 600,
            }}>
            No hay instituciones disponibles
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <Alert severity="warning" sx={{ mb: 2, mt: 2 }}>
              No se encontraron instituciones (tenants) configuradas para el cantón{' '}
              <strong>{currentCity}</strong>.
            </Alert>
            <MIUTypography variant="body1" sx={{ mb: 1 }}>
              Para registrar árboles es necesario tener al menos una institución asociada
              al cantón. Solicite o cree una institución.
            </MIUTypography>
            <MIUTypography variant="body2" color="text.secondary">
              Puede crear instituciones desde el módulo de Administración Territorial.
            </MIUTypography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => navigate(buildCantonUrl(currentCity, cantones))}>
              Cancelar
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={reloadCantones}
              disabled={citiesLoading}>
              Reintentar
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddBusinessIcon />}
              onClick={() => navigate(TERRITORIAL_MANAGEMENT_DEFAULT_ROUTE)}>
              Crear Institución
            </Button>
          </DialogActions>
        </Dialog>
      </Portal>
    )
  }

  return (
    <Slide
      direction={isMobile ? 'up' : 'left'}
      in
      mountOnEnter
      unmountOnExit
      timeout={400}>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          px: 1,
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          minHeight: isMobile ? '60vh' : '100vh',
        }}>
        <TreeRegistrationWizardV2 />
      </Box>
    </Slide>
  )
}

export default RegisterTreeWizardViewV2
